/**
 * DeleteAccountModal — final confirmation dialog before account deletion.
 *
 * Safety pattern (anti-misclick): user must TYPE their exact email to enable
 * the destructive button. Case-insensitive match. Optional "reason" text area
 * for product insights (anonymized in audit log).
 *
 * UX: red header, clear list of what gets deleted, 30-day recovery window
 * highlighted, PRO subscription warning when applicable.
 */
import React, { useState } from 'react';

export default function DeleteAccountModal({
  userEmail,
  isPro,
  subscriptionEndsOn,
  onCancel,
  onConfirm,
}) {
  const [typedEmail, setTypedEmail] = useState('');
  const [reason,     setReason]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');

  const emailMatches =
    typedEmail.trim().toLowerCase() === (userEmail || '').toLowerCase();
  const canDelete = emailMatches && !submitting;

  const handleDelete = async () => {
    if (!canDelete) return;
    setError('');
    setSubmitting(true);
    try {
      await onConfirm(typedEmail.trim(), reason.trim());
      // Parent handles redirect — no need to setSubmitting(false)
    } catch (err) {
      setSubmitting(false);
      const msg = err?.response?.data?.error || err?.message
                || 'Could not delete account. Please try again or contact support.';
      setError(msg);
    }
  };

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1.5rem', animation: 'fadeIn 0.18s ease-out',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: '16px',
          width: '100%', maxWidth: '500px', maxHeight: '92vh',
          overflowY: 'auto',
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
      >
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #b91c1c, #ef4444)',
          padding: '1.1rem 1.5rem', color: 'white',
          borderRadius: '16px 16px 0 0',
        }}>
          <h2 id="delete-account-title" style={{
            margin: 0, fontFamily: "'Syne', sans-serif",
            fontSize: '1.25rem', fontWeight: 800, letterSpacing: '-0.01em',
            display: 'flex', alignItems: 'center', gap: '0.5rem',
          }}>
            <span>⚠</span> Delete your account
          </h2>
          <p style={{
            margin: '0.35rem 0 0', fontSize: '0.82rem', color: 'rgba(255,255,255,0.85)',
          }}>
            This action cannot be undone after 30 days.
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: '1.25rem 1.5rem' }}>

          {/* Account being deleted */}
          <div style={{
            background: '#f8fafc', border: '1px solid #e2e8f0',
            borderRadius: '10px', padding: '0.7rem 0.9rem',
            fontSize: '0.85rem', color: '#334155',
            marginBottom: '1rem',
          }}>
            <span style={{ color: '#64748b' }}>Account: </span>
            <strong>{userEmail || '(unknown)'}</strong>
          </div>

          {/* What happens */}
          <p style={{
            margin: '0 0 0.5rem', fontSize: '0.85rem', fontWeight: 700, color: '#0f172a',
          }}>
            What happens:
          </p>
          <ul style={{
            margin: '0 0 1.1rem', padding: '0 0 0 1.25rem',
            fontSize: '0.82rem', color: '#475569', lineHeight: 1.7,
          }}>
            <li>You'll be signed out immediately and can't sign back in</li>
            <li>Your data (deals, invoices, profile, settings) stays preserved for 30 days</li>
            <li>After 30 days, everything is permanently deleted</li>
            <li>Financial records (invoices/payments) are retained 7 years per Indian tax law (no PII tied to them after deletion)</li>
          </ul>

          {/* PRO subscription warning */}
          {isPro && (
            <div style={{
              background: '#fff7ed', border: '1px solid #fed7aa',
              borderRadius: '10px', padding: '0.7rem 0.9rem',
              fontSize: '0.82rem', color: '#9a3412', lineHeight: 1.55,
              marginBottom: '1rem',
            }}>
              <strong>⚡ PRO subscription will be cancelled.</strong> You'll keep access until
              {subscriptionEndsOn
                ? ` ${new Date(subscriptionEndsOn).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
                : ' the end of your current billing period'}.
              No refund for unused time.
              <br/><br/>
              <strong>Note:</strong> if you restore the account within 30 days, your PRO subscription is <strong>not</strong> automatically reactivated — you'll need to re-subscribe from the Pricing page.
            </div>
          )}

          {/* Recovery info */}
          <div style={{
            background: '#fef3c7', border: '1px solid #fcd34d',
            borderRadius: '10px', padding: '0.7rem 0.9rem',
            fontSize: '0.82rem', color: '#78350f', lineHeight: 1.55,
            marginBottom: '1.25rem',
          }}>
            <strong>⏱ 30-day recovery — full restore.</strong> Email{' '}
            <a href="mailto:support@creatorkhata.com" style={{ color: '#78350f', fontWeight: 700 }}>
              support@creatorkhata.com
            </a>{' '}
            from this address within 30 days and we'll switch the account back on — all your data exactly as it was. After 30 days, deletion is permanent.
          </div>

          {/* Email confirmation */}
          <label style={{
            display: 'block', fontSize: '0.82rem', fontWeight: 700,
            color: '#0f172a', marginBottom: '0.4rem',
          }}>
            Type your email to confirm <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="email"
            value={typedEmail}
            onChange={e => setTypedEmail(e.target.value)}
            placeholder={userEmail}
            autoFocus
            style={{
              width: '100%', padding: '0.6rem 0.8rem',
              border: '1.5px solid ' + (typedEmail && !emailMatches ? '#fca5a5' : '#e2e8f0'),
              borderRadius: '10px', fontSize: '0.88rem',
              outline: 'none', fontFamily: 'inherit',
              boxSizing: 'border-box',
              marginBottom: '0.85rem',
            }}
            aria-label="Type your email to confirm deletion"
          />

          {/* Optional reason */}
          <label style={{
            display: 'block', fontSize: '0.82rem', fontWeight: 700,
            color: '#0f172a', marginBottom: '0.4rem',
          }}>
            Reason for leaving (optional)
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="What didn't work for you? (helps us improve)"
            rows={2}
            maxLength={400}
            style={{
              width: '100%', padding: '0.55rem 0.8rem',
              border: '1.5px solid #e2e8f0',
              borderRadius: '10px', fontSize: '0.85rem',
              resize: 'vertical', minHeight: '60px',
              fontFamily: 'inherit',
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: '0.5rem',
            }}
          />

          {/* Error display */}
          {error && (
            <div style={{
              padding: '0.65rem 0.85rem', marginTop: '0.75rem',
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: '10px', fontSize: '0.82rem', color: '#b91c1c',
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div style={{
          padding: '1rem 1.5rem 1.25rem',
          borderTop: '1px solid #f1f5f9',
          display: 'flex', gap: '0.6rem', justifyContent: 'flex-end',
        }}>
          <button
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: '0.6rem 1.1rem',
              background: 'white', color: '#334155',
              border: '1.5px solid #e2e8f0', borderRadius: '10px',
              fontSize: '0.85rem', fontWeight: 700,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={!canDelete}
            style={{
              padding: '0.6rem 1.2rem',
              background: canDelete ? '#ef4444' : '#fca5a5',
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '0.85rem', fontWeight: 700,
              cursor: canDelete ? 'pointer' : 'not-allowed',
            }}
          >
            {submitting ? 'Deleting…' : 'Delete forever'}
          </button>
        </div>
      </div>
    </div>
  );
}
